import { useEffect, useState } from "react";
import {
  X, FileText, Image as ImageIcon, Check,
} from "lucide-react";
import BrowseBar from "./BrowseBar";
import UploadFileCard from "./UploadFileCard";

const BLUE = "#2B579A";
const BLUE_SOFT = "#4A8CD8";

/**
 * FileOrScanInput — file picker used everywhere in Le Desk.
 *
 * Historically had two tabs (Parcourir / Scanner) — the "Scanner" tab was
 * removed because native browser-side driving of physical scanners
 * (TWAIN/WIA/eSCL) requires either a paid SDK or per-machine bridge software
 * that we didn't want to impose on Vertec users. The upload flow has moved
 * to a mail-based ingestion path (scan-to-email) handled elsewhere.
 *
 * The component now shows only the compact <BrowseBar /> directly (drag/drop
 * or click to select).
 *
 * Two consumption modes, unchanged from the previous version:
 *
 *  A) List mode (preferred): pass `entries`, `onFiles`, `onCancel`, `onRemove`.
 *  B) Legacy single-slot mode: pass `value`, `onFile`, `onClear`.
 */
export default function FileOrScanInput({
  // List mode
  entries,
  onFiles,
  onCancel,
  onRemove,
  // Legacy single-slot mode
  value,
  onFile,
  onClear,
  // Common
  accept = "*/*",
  multiple = false,
  testIdPrefix = "fos",
}) {
  const listMode = typeof onFiles === "function";
  const hasEntries = listMode && entries && entries.length > 0;
  const hasLegacyValue = !listMode && !!value;

  const emitPicked = (files) => {
    if (listMode) onFiles(files);
    else if (files?.[0]) onFile?.(files[0]);
  };

  // Spec rev153 (2026-02-07) — BrowseBar est désormais un bloc vertical
  // (~92px). L'idle mode se contente d'un simple wrapper sans hauteur
  // forcée (auparavant `min-h-10` pour l'alignement dans la grille des
  // dynamic-fields, incompatible avec la nouvelle zone verticale).
  const idle = !hasEntries && !hasLegacyValue;

  return (
    <div
      className={
        idle
          ? ""
          : "rounded-xl overflow-hidden p-3 space-y-2"
      }
      style={
        idle
          ? undefined
          : { background: "#1A1A1A", border: `1px solid rgba(74,140,216,0.25)` }
      }
      data-testid={`${testIdPrefix}-root`}
    >
      {hasLegacyValue ? (
        <FilePreview value={value} onClear={onClear} testIdPrefix={testIdPrefix} />
      ) : (
        <BrowseBar
          accept={accept}
          multiple={multiple}
          onFiles={emitPicked}
          testIdPrefix={`${testIdPrefix}-browse`}
        />
      )}

      {/* --- Live queue list (list mode) ------------------------------- */}
      {hasEntries && (
        <div className="space-y-1.5 pt-1">
          {entries.map((e, idx) => (
            <UploadFileCard
              key={e.id}
              file={e.file}
              status={e.status}
              progress={e.progress}
              uploadedBytes={e.uploadedBytes}
              errorMessage={e.error}
              onCancel={() => onCancel?.(e.id)}
              onRemove={() => onRemove?.(e.id)}
              testId={`${testIdPrefix}-entry-${idx}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legacy single-slot preview (DocumentCreateDialog file-fields).
function FilePreview({ value, onClear, testIdPrefix }) {
  const isImage = (value?.type || "").startsWith("image/");
  const isPdf = (value?.type || "") === "application/pdf";
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    if (!isImage) return;
    const url = URL.createObjectURL(value);
    setThumb(url);
    return () => URL.revokeObjectURL(url);
  }, [value, isImage]);

  const sizeKb = value?.size ? Math.round(value.size / 1024) : 0;
  // Spec rev154 (2026-02-07) — vignette de confirmation VERTE identique
  // à celle utilisée par UploadFileCard (mode "done") : fond vert foncé,
  // bord vert vif, icône type de fichier à gauche, nom+taille au centre
  // avec coche « Chargé » à droite, ✕ pour retirer.
  return (
    <div
      className="flex items-center gap-3 rounded-lg"
      style={{
        background: "#0F2A1C",
        border: "1.5px solid #22C55E",
        padding: "11px 13px",
      }}
      data-testid={`${testIdPrefix}-preview`}
    >
      <div
        className="shrink-0 rounded flex items-center justify-center overflow-hidden"
        style={{
          width: 32, height: 32,
          background: "rgba(34,197,94,0.18)",
          color: "#22C55E",
        }}
      >
        {isImage && thumb ? (
          <img src={thumb} alt="" className="h-full w-full object-cover" />
        ) : isImage ? (
          <ImageIcon size={16} />
        ) : (
          <FileText size={16} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div
            className="text-[12.5px] font-medium truncate"
            style={{ color: "#EDEDED" }}
            title={value.name}
          >
            {value.name}
          </div>
          <div
            className="text-[11px] font-bold tabular-nums shrink-0 inline-flex items-center gap-1"
            style={{ color: "#22C55E" }}
          >
            <Check size={12} strokeWidth={2.8} />
            Chargé
          </div>
        </div>
        <div
          className="mt-0.5 text-[10.5px] tabular-nums truncate"
          style={{ color: "rgba(255,255,255,0.55)" }}
        >
          {sizeKb.toLocaleString("fr-FR")} Ko · {isPdf ? "PDF" : (value.type || "type inconnu")}
        </div>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="transition-colors"
        style={{ color: "rgba(255,255,255,0.55)" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#F87171")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.55)")}
        title="Retirer"
        data-testid={`${testIdPrefix}-clear`}
      >
        <X size={15} strokeWidth={2.2} />
      </button>
    </div>
  );
}

// Named alias kept for compatibility with any external import.
export { UploadFileCard };
